import { useState, useMemo, useEffect } from 'react';
import NavBar from './components/NavBar';
import FormRecepcion from './components/FormRecepcion';
import RecepcionOrientacion from './components/RecepcionOrientacion';
import EvaluacionProyecto from './components/EvaluacionProyecto';
import AsignacionCodigo from './components/AsignacionCodigo';
import ResultadoDecision from './components/ResultadoDecision';
import SeguimientoReportes from './components/SeguimientoReportes';

import Dashboard from './components/Dashboard';
import AuthScreen from './components/AuthScreen';
import InvestigadorPanel from './components/InvestigadorPanel';
import CartaSolicitudEditor from './components/CartaSolicitudEditor';
import CartasRecibidas from './components/CartasRecibidas';
import { useSupabaseProyectos } from '../../hooks/useSupabaseProyectos';
import { useSupabaseCartas } from '../../hooks/useSupabaseCartas';
import { useAuth } from '../../hooks/useAuth';

type Section =
  | 'dashboard'
  | 'recepcion'
  | 'evaluacion'
  | 'asignacion'
  | 'resultado'
  | 'seguimiento'

  | 'mis-proyectos'
  | 'carta-solicitud'
  | 'cartas-recibidas';

type RoleChoice = 'investigador' | 'ugpp' | null;

const ugppSections = [
  { id: 'dashboard', label: 'Panel de Control', icon: 'ri-dashboard-line', desc: 'Resumen y visualizaciones' },
  { id: 'cartas-recibidas', label: 'Cartas de Solicitud', icon: 'ri-inbox-line', desc: 'Cartas de investigadores' },
  { id: 'recepcion', label: 'Formulario de Recepción', icon: 'ri-file-text-line', desc: 'Presentar proyecto' },
  { id: 'evaluacion', label: 'Evaluación', icon: 'ri-microscope-line', desc: 'Panel evaluador' },
  { id: 'asignacion', label: 'Código y Categoría', icon: 'ri-price-tag-3-line', desc: 'Registro oficial' },
  { id: 'resultado', label: 'Resultado y Decisión', icon: 'ri-award-line', desc: 'Dictamen científico' },
  { id: 'seguimiento', label: 'Seguimiento', icon: 'ri-bar-chart-line', desc: 'Informes de avance' },

];

const investigadorSections = [
  { id: 'mis-proyectos', label: 'Mis Proyectos', icon: 'ri-folder-open-line', desc: 'Estado y evaluaciones' },
  { id: 'carta-solicitud', label: 'Carta de Solicitud', icon: 'ri-mail-send-line', desc: 'Solicitar evaluación' },
  { id: 'recepcion', label: 'Presentar Proyecto', icon: 'ri-file-text-line', desc: 'Formulario de recepción' },

];

export default function HomePage() {
  const { currentUser, logout, isLoading } = useAuth();
  const [roleChoice, setRoleChoice] = useState<RoleChoice>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>('recepcion');


  const { proyectos, loading: proyectosLoading, addProyecto, updateProyecto } = useSupabaseProyectos();
  const { cartas, loading: cartasLoading, addCarta, updateCarta, marcarLeida } = useSupabaseCartas();

  // When user logs in, redirect to the right section
  useEffect(() => {
    if (currentUser) {
      setShowAuth(false);
      setRoleChoice(null);
      if (currentUser.role === 'ugpp') {
        setActiveSection('dashboard');
      } else {
        setActiveSection('mis-proyectos');
      }
    }
  }, [currentUser?.id]);

  const cartasNuevas = cartas.filter(c => c.estado === 'enviada').length;

  const heroStats = useMemo(() => [
    { label: 'Proyectos recibidos', value: String(proyectos.length), icon: 'ri-file-text-line' },
    { label: 'En evaluación', value: String(proyectos.filter(p => p.estado === 'En evaluación' || p.estado === 'Pendiente evaluación').length), icon: 'ri-microscope-line' },
    { label: 'Aprobados', value: String(proyectos.filter(p => ['Aprobado', 'En ejecución', 'Finalizado'].includes(p.estado)).length), icon: 'ri-checkbox-circle-line' },
    { label: 'Con correcciones', value: String(proyectos.filter(p => p.estado === 'Con correcciones').length), icon: 'ri-edit-circle-line' },
  ], [proyectos]);

  const effectiveRole = currentUser?.role ?? null;
  const sections = effectiveRole === 'ugpp' ? ugppSections : investigadorSections;

  const handleRoleSelect = (role: RoleChoice) => {
    setRoleChoice(role);
    setShowAuth(true);
  };

  const handleLogout = async () => {
    await logout();
    setRoleChoice(null);
    setShowAuth(false);
    setActiveSection('recepcion');
  };

  const handleSubmitSuccess = () => {
    setActiveSection('mis-proyectos');
  };



  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard': return <Dashboard proyectos={proyectos} />;
      case 'mis-proyectos':
        return currentUser ? (
          <InvestigadorPanel
            user={currentUser}
            proyectos={proyectos}
            onNuevoProyecto={() => setActiveSection('recepcion')}

          />
        ) : null;
      case 'carta-solicitud':
        return currentUser ? (
          <CartaSolicitudEditor
            user={currentUser}
            proyectos={proyectos}
            cartas={cartas}
            onAddCarta={addCarta}
            onUpdateCarta={updateCarta}
          />
        ) : null;
      case 'cartas-recibidas':
        return (
          <CartasRecibidas
            cartas={cartas}
            onMarcarLeida={marcarLeida}
          />
        );
      case 'recepcion':
        return effectiveRole === 'ugpp'
          ? <RecepcionOrientacion proyectos={proyectos} />
          : <FormRecepcion onSubmitSuccess={handleSubmitSuccess} onAddProyecto={addProyecto} />;
      case 'evaluacion': return <EvaluacionProyecto proyectos={proyectos} onUpdateProyecto={updateProyecto} />;
      case 'asignacion': return <AsignacionCodigo />;
      case 'resultado': return <ResultadoDecision />;
      case 'seguimiento': return <SeguimientoReportes />;

      default:
        return effectiveRole === 'ugpp'
          ? <RecepcionOrientacion proyectos={proyectos} />
          : <FormRecepcion onSubmitSuccess={handleSubmitSuccess} onAddProyecto={addProyecto} />;
    }
  };

  // Show auth screen
  if (showAuth && roleChoice && !currentUser) {
    return (
      <AuthScreen
        role={roleChoice}
        onSuccess={() => {/* useEffect handles redirect */}}
        onBack={() => { setShowAuth(false); setRoleChoice(null); }}
      />
    );
  }

  // Role selection screen (not logged in)
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />

        <div className="bg-[#00426A] px-8 py-10" style={{borderBottom: '3px solid #F5A623'}}>
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-24 h-24 flex items-center justify-center bg-white rounded-xl p-2 shadow-md flex-shrink-0">
                <img
                  src="https://ugpp.upal.edu/semilla_azul.png"
                  alt="Logo UPAL"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="text-[#F5A623] text-xs font-bold tracking-widest uppercase mb-0.5">
                  Universidad Privada Abierta Latinoamericana
                </p>
                <h1 className="text-2xl font-bold text-white">Vicerrectorado de Investigación, innovacion y desarrollo institucional</h1>
                <p className="text-white/60 text-sm">Sistema UGPP - Unidad de Gestión de Proyectos y Publicaciones</p>
                <p className="text-white/40 text-xs mt-0.5">Comité de Investigación e Interacción</p>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Seleccione su perfil de acceso</h2>
            <p className="text-white/60 text-base">
              Ingrese al portal según su rol para acceder a las opciones correspondientes
            </p>
          </div>
        </div>

        <div className="flex-1 max-w-screen-xl mx-auto w-full px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Investigador card */}
            <button
              onClick={() => handleRoleSelect('investigador')}
              className="cursor-pointer text-left bg-white border-2 border-slate-200 hover:border-teal-400 rounded-xl p-8 transition-all group"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-xl mb-5 group-hover:bg-teal-100 transition-colors">
                <i className="ri-user-3-line text-3xl text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Investigador / Solicitante</h3>
              <p className="text-slate-500 text-sm mb-5">
                Crea tu cuenta, presenta tu propuesta de investigación y recibe el código de seguimiento.
              </p>
              <ul className="space-y-2 text-sm text-slate-600 mb-6">
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Registro con correo y contraseña
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Código de proyecto generado automáticamente
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Ver evaluaciones del Comité en tiempo real
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Subida de documentos requeridos
                </li>
              </ul>
              <div className="whitespace-nowrap inline-flex items-center gap-2 bg-[#00426A] text-white px-5 py-2.5 rounded-lg text-sm font-semibold group-hover:bg-[#02ACB5] transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-login-box-line" /></span>
                Ingresar / Registrarse
              </div>
            </button>

            {/* UGPP card */}
            <button
              onClick={() => handleRoleSelect('ugpp')}
              className="cursor-pointer text-left bg-white border-2 border-slate-200 hover:border-teal-400 rounded-xl p-8 transition-all group"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-xl mb-5 group-hover:bg-teal-100 transition-colors">
                <i className="ri-shield-user-line text-3xl text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">UGPP / Comité de Investigación e Interacción</h3>
              <p className="text-slate-500 text-sm mb-5">
                Acceso exclusivo para miembros del Comité. Requiere código institucional.
              </p>
              <ul className="space-y-2 text-sm text-slate-600 mb-6">
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Registro con correo, contraseña y código institucional
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Panel de control con gráficas y resúmenes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line" /></span>
                  Evaluación, códigos, dictámenes y seguimiento
                </li>
              </ul>
              <div className="whitespace-nowrap inline-flex items-center gap-2 bg-[#00426A] text-white px-5 py-2.5 rounded-lg text-sm font-semibold group-hover:bg-[#02ACB5] transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-keyhole-line" /></span>
                Acceder con código institucional
              </div>
            </button>
          </div>
        </div>

        <footer className="bg-[#00426A] text-white/50 py-5">
          <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-center">
            <p className="text-xs">UGPP – Vicerrectorado de Investigación, innovacion y desarrollo institucional · Sistema de Gestión de Proyectos y Publicaciones · 2026</p>
          </div>
        </footer>
      </div>
    );
  }

  const currentSectionMeta = sections.find((s) => s.id === activeSection);

  // Main layout (logged in)
  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />

      <NavBar
        activeSection={activeSection}
        onSectionChange={(s) => setActiveSection(s as Section)}
        sections={sections}
        role={effectiveRole}
        onChangeRole={handleLogout}
      />

      {/* Hero banner */}
      <div className="pt-20 bg-[#00426A]" style={{borderBottom: '3px solid #F5A623'}}>
        <div className="w-full px-8 py-7">
          {/* Breadcrumb + user info */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleLogout}
                className="whitespace-nowrap flex items-center gap-1.5 text-teal-200 hover:text-white text-xs font-medium px-3 py-1.5 rounded-md border border-teal-500 hover:border-white transition-all cursor-pointer bg-white/10 hover:bg-white/20"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
                Inicio
              </button>
              <span className="text-teal-500 text-xs">/</span>
              <span className="text-teal-200 text-xs font-medium">
                {effectiveRole === 'investigador' ? 'Portal del Investigador' : 'UGPP / Comité'}
              </span>
              {currentSectionMeta && (
                <>
                  <span className="text-teal-500 text-xs">/</span>
                  <span className="text-white text-xs font-semibold">{currentSectionMeta.label}</span>
                </>
              )}
            </div>
            {/* User chip */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5">
                <span className="w-5 h-5 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-user-line text-white text-xs" />
                </span>
                <span className="text-white text-xs font-medium">{currentUser.nombre}</span>
                <span className="text-teal-300 text-xs hidden sm:inline">·</span>
                <span className="text-teal-200 text-xs hidden sm:inline">{currentUser.email}</span>
              </div>
              <button
                onClick={handleLogout}
                title="Cerrar sesión"
                className="whitespace-nowrap flex items-center gap-1.5 text-teal-200 hover:text-white text-xs font-medium px-3 py-1.5 rounded-md border border-teal-500 hover:border-white transition-all cursor-pointer bg-white/10 hover:bg-white/20"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-logout-box-line" /></span>
                Cerrar sesión
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
            <div>
              <p className="text-teal-200 text-xs font-semibold uppercase tracking-widest mb-1">
                {effectiveRole === 'investigador' ? 'Portal del Investigador' : 'Panel UGPP / Comité de Investigación e Interacción'}
              </p>
              <h2 className="text-2xl font-bold text-white leading-tight">
                Sistema de Gestión de Proyectos de Investigación
              </h2>
              <p className="text-teal-200 text-sm mt-1">Recepción · Evaluación · Seguimiento · Decisión oficial</p>
            </div>
            {effectiveRole === 'ugpp' && (
              <button
                onClick={() => setActiveSection('dashboard')}
                className="whitespace-nowrap flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer border border-white/30"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-dashboard-line" /></span>
                Ver panel de control
              </button>
            )}
          </div>

          {effectiveRole === 'ugpp' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-1">
              {heroStats.map((s, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center gap-3 border border-white/10"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-md">
                    <i className={`${s.icon} text-lg text-white`} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white leading-none">{s.value}</p>
                    <p className="text-teal-200 text-xs mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {effectiveRole === 'investigador' && (
            <div className="bg-white/10 border border-white/20 rounded-lg p-4 max-w-lg">
              <p className="text-white text-sm font-medium mb-1 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-check-line" /></span>
                Notificación del resultado por correo electrónico
              </p>
              <p className="text-teal-200 text-xs">
                Cuando el Comité de Investigación e Interacción emita su dictamen, recibirá automáticamente un correo con el resultado y las indicaciones correspondientes.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="bg-white border-b border-slate-200 xl:hidden overflow-x-auto">
        <div className="flex w-max px-4 py-2 gap-1">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id as Section)}
              className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer ${
                activeSection === s.id ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-teal-50 hover:text-teal-700'
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={s.icon} /></span>
              {s.label}
              {s.id === 'cartas-recibidas' && cartasNuevas > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {cartasNuevas}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main layout */}
      <div className="w-full max-w-screen-xl mx-auto px-6 py-8 flex gap-6">
        {/* Sidebar */}
        <aside className="hidden xl:flex flex-col gap-1 w-60 flex-shrink-0">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest px-2 mb-1">Navegación</p>
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id as Section)}
              className={`whitespace-nowrap w-full text-left px-3 py-2.5 rounded-lg transition-all cursor-pointer flex items-start gap-2.5 ${
                activeSection === s.id
                  ? 'bg-[#00426A] text-white'
                  : 'text-slate-600 hover:bg-teal-50 hover:text-teal-700'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${s.icon} text-base`} />
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="block text-sm font-medium leading-tight">{s.label}</span>
                  {s.id === 'cartas-recibidas' && cartasNuevas > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {cartasNuevas}
                    </span>
                  )}
                </span>
                <span className={`block text-xs mt-0.5 ${activeSection === s.id ? 'text-teal-200' : 'text-slate-400'}`}>
                  {s.desc}
                </span>
              </span>
            </button>
          ))}

          {/* User info in sidebar */}
          <div className="mt-4 bg-teal-50 border border-teal-100 rounded-lg p-3">
            <p className="text-xs font-semibold text-teal-700 mb-1 flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-line" /></span>
              Sesión activa
            </p>
            <p className="text-xs text-teal-800 font-medium truncate">{currentUser.nombre}</p>
            <p className="text-xs text-teal-600 truncate">{currentUser.email}</p>
            <button
              onClick={handleLogout}
              className="whitespace-nowrap mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-md px-2 py-1.5 transition-colors cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-logout-box-line" /></span>
              Cerrar sesión
            </button>
          </div>

          {effectiveRole === 'ugpp' && (
            <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-3">
              <p className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                Flujo del proceso
              </p>
              <ol className="space-y-1.5">
                {['Recepción del proyecto', 'Asignación de código', 'Evaluación científica', 'Emisión de decisión', 'Seguimiento y reportes'].map((step, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                    <span className="font-bold flex-shrink-0">{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-white rounded-lg border border-slate-200 p-6 md:p-8 min-h-96">
            {(proyectosLoading || cartasLoading) && (
              <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
                <span className="w-4 h-4 flex items-center justify-center mr-2"><i className="ri-loader-4-line animate-spin" /></span>
                Cargando datos...
              </div>
            )}
            {!(proyectosLoading && cartasLoading) && renderSection()}
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#00426A] text-white/50 py-6 mt-8">
        <div className="w-full max-w-screen-xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg p-1">
              <img
                src="https://ugpp.upal.edu/semilla_azul.png"
                alt="Logo UPAL"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-sm font-medium text-white">UGPP – Vicerrectorado de Investigación, innovacion y desarrollo institucional</span>
          </div>
          <p className="text-xs">Sistema de Gestión de Proyectos de Investigación · 2026</p>
        </div>
      </footer>
    </div>
  );
}
